(() => {
  const selector = '[data-ss-product-reels]';
  const registry = window.__ssProductReelsRegistry || {
    bound: false,
    lastButton: null,
    lastOpenedAt: 0,
    roots: new WeakMap()
  };

  window.__ssProductReelsRegistry = registry;

  function getVideo(card) {
    return card ? card.querySelector('video') : null;
  }

  function getVideoSource(video) {
    if (!video) return '';
    const source = video.querySelector('source');
    return video.currentSrc || video.getAttribute('src') || (source ? source.getAttribute('src') : '') || '';
  }

  function setVideoDefaults(video) {
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.removeAttribute('controls');
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      };
      return entities[character];
    });
  }

  function openTrigger(trigger, event) {
    const button = trigger?.closest?.('[data-ss-reels-open], [data-ss-reels-card]');

    if (!button || button.disabled) {
      return false;
    }

    const root = button.closest(selector);

    if (!root) {
      return false;
    }

    if (!registry.roots.get(root)) {
      init(root);
    }

    const instance = registry.roots.get(root);

    if (!instance) {
      return false;
    }

    const now = Date.now();

    if (registry.lastButton === button && now - registry.lastOpenedAt < 350) {
      return true;
    }

    registry.lastButton = button;
    registry.lastOpenedAt = now;

    event?.preventDefault?.();
    event?.stopPropagation?.();
    instance.openFromButton(button);

    return true;
  }

  function init(root) {
    if (!root || root.dataset.ssProductReelsReady === 'true') return;
    root.dataset.ssProductReelsReady = 'true';

    const viewport = root.querySelector('[data-ss-reels-viewport]');
    const track = root.querySelector('[data-ss-reels-track]');
    const cards = Array.from(root.querySelectorAll('[data-ss-reels-card]')).filter((card) => {
      const button = card.querySelector('[data-ss-reels-open]');
      return button && !button.disabled;
    });
    const previousButton = root.querySelector('[data-ss-reels-previous]');
    const nextButton = root.querySelector('[data-ss-reels-next]');
    const popup = root.querySelector('[data-ss-reels-popup]');
    const popupVideo = root.querySelector('[data-ss-reels-popup-video]');
    const popupImage = root.querySelector('[data-ss-reels-popup-image]');
    const popupProduct = root.querySelector('[data-ss-reels-product]');
    const closeButton = root.querySelector('[data-ss-reels-close]');
    const popupPrevious = root.querySelector('[data-ss-reels-popup-previous]');
    const popupNext = root.querySelector('[data-ss-reels-popup-next]');

    if (!viewport || !track || !cards.length || !popup || !popupVideo || !popupImage) return;

    const rootStyles = window.getComputedStyle(root);
    popup.style.setProperty('--ss-reels-popup-overlay', rootStyles.getPropertyValue('--ss-reels-popup-overlay') || '0.2');
    popup.style.setProperty('--ss-reels-popup-radius', rootStyles.getPropertyValue('--ss-reels-popup-radius') || '14px');

    if (popup.parentNode !== document.body) {
      document.body.appendChild(popup);
    }

    cards.forEach((card) => {
      const video = getVideo(card);
      setVideoDefaults(video);

      if (!card.dataset.videoSrc && video) {
        card.dataset.videoSrc = getVideoSource(video);
      }
    });

    const mediaCards = cards.filter((card) => card.dataset.videoSrc || card.dataset.imageSrc);
    const videoPreloaders = new Map();
    const imagePreloaders = new Map();

    let previewTimer = null;
    let previewIndex = 0;
    let popupIndex = 0;
    let mediaRenderToken = 0;
    let previousBodyOverflow = '';
    let isPointerDown = false;
    let pointerStartX = 0;
    let scrollStartX = 0;
    let didDrag = false;
    let pointerStartButton = null;
    let suppressNextClick = false;

    const previewDuration = Math.max(2000, Number.parseInt(root.dataset.previewDuration || '4000', 10));
    const previewCards = cards.filter((card) => Boolean(getVideo(card)));

    function getStepSize() {
      const firstCard = cards[0];
      if (!firstCard) return viewport.clientWidth;
      const style = window.getComputedStyle(track);
      const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0;
      return firstCard.getBoundingClientRect().width + gap;
    }

    function updateArrows() {
      if (!previousButton && !nextButton) return;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const atStart = viewport.scrollLeft <= 2;
      const atEnd = viewport.scrollLeft >= maxScroll - 2;

      if (previousButton) {
        previousButton.classList.toggle('is-hidden', atStart);
        previousButton.disabled = atStart;
      }

      if (nextButton) {
        nextButton.classList.toggle('is-hidden', atEnd);
        nextButton.disabled = atEnd;
      }
    }

    function scrollByDirection(direction) {
      viewport.scrollBy({ left: getStepSize() * direction, behavior: 'smooth' });
      window.setTimeout(updateArrows, 320);
    }

    function pausePreviewVideos() {
      cards.forEach((card) => {
        const video = getVideo(card);
        if (!video) return;
        video.pause();
        try {
          video.currentTime = 0;
        } catch (error) {
          // Some streamed media does not allow seeking until metadata is ready.
        }
      });
    }

    function playPreviewCard(index) {
      if (!previewCards.length) return;
      previewIndex = ((index % previewCards.length) + previewCards.length) % previewCards.length;
      pausePreviewVideos();

      const card = previewCards[previewIndex];
      const video = getVideo(card);
      setVideoDefaults(video);
      video?.play().catch(() => {});

      const targetLeft = card.offsetLeft - (viewport.clientWidth - card.offsetWidth) / 2;
      viewport.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
      window.setTimeout(updateArrows, 320);
    }

    function startPreviewCycle() {
      if (!previewCards.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      stopPreviewCycle();
      playPreviewCard(previewIndex);
      previewTimer = window.setInterval(() => {
        playPreviewCard(previewIndex + 1);
      }, previewDuration);
    }

    function stopPreviewCycle() {
      if (previewTimer) {
        window.clearInterval(previewTimer);
        previewTimer = null;
      }
    }

    function updatePopupNav() {
      const shouldHide = mediaCards.length < 2;
      popupPrevious?.classList.toggle('is-hidden', shouldHide);
      popupNext?.classList.toggle('is-hidden', shouldHide);
    }

    function preloadVideoSource(src) {
      if (!src || videoPreloaders.has(src)) return;

      const video = document.createElement('video');
      setVideoDefaults(video);
      video.preload = 'auto';
      video.src = src;
      video.load();
      videoPreloaders.set(src, video);
    }

    function preloadImageSource(src) {
      if (!src || imagePreloaders.has(src)) return;

      const image = new Image();
      image.src = src;
      imagePreloaders.set(src, image);
    }

    function preloadMediaAround(index) {
      if (!mediaCards.length) return;

      [index, index + 1].forEach((targetIndex) => {
        const normalizedIndex = ((targetIndex % mediaCards.length) + mediaCards.length) % mediaCards.length;
        const card = mediaCards[normalizedIndex];

        if (!card) return;

        preloadVideoSource(card.dataset.videoSrc || '');
        preloadImageSource(card.dataset.imageSrc || '');
      });
    }

    function renderProduct(card) {
      if (!popupProduct) return;
      const title = card.dataset.productTitle || '';
      const url = card.dataset.productUrl || '';
      const image = card.dataset.productImage || '';

      if (!title) {
        popupProduct.innerHTML = '';
        return;
      }

      const safeTitle = escapeHtml(title);
      const safeUrl = escapeHtml(url);
      const safeImage = escapeHtml(image);
      const imageHtml = image ? `<img class="ss-product-reels__featured-image" src="${safeImage}" alt="${safeTitle}">` : '';
      const titleHtml = url ? `<a href="${safeUrl}">${safeTitle}</a>` : safeTitle;

      popupProduct.innerHTML = `
        <div class="ss-product-reels__featured-card">
          ${imageHtml}
          <div>
            <p class="ss-product-reels__featured-label">Featured product</p>
            <p class="ss-product-reels__featured-title">${titleHtml}</p>
          </div>
        </div>
      `;
    }

    function renderPopupMedia() {
      const card = mediaCards[popupIndex];
      if (!card) return;

      const renderToken = ++mediaRenderToken;
      const videoSrc = card.dataset.videoSrc || '';
      const imageSrc = card.dataset.imageSrc || '';
      const finishLoading = () => {
        if (renderToken === mediaRenderToken) {
          popup.classList.remove('is-loading');
        }
      };

      popupVideo.pause();
      popupVideo.removeAttribute('src');
      popupVideo.innerHTML = '';
      popupVideo.hidden = true;
      popupImage.hidden = true;
      popupImage.removeAttribute('src');
      popup.classList.add('is-loading');

      if (videoSrc) {
        preloadVideoSource(videoSrc);
        popupVideo.addEventListener('loadeddata', finishLoading, { once: true });
        popupVideo.addEventListener('canplay', finishLoading, { once: true });
        popupVideo.addEventListener('error', finishLoading, { once: true });
        popupVideo.src = videoSrc;
        popupVideo.hidden = false;
        setVideoDefaults(popupVideo);
        popupVideo.preload = 'auto';
        popupVideo.load();
        popupVideo.play().catch(() => {});

        if (popupVideo.readyState >= 2) {
          finishLoading();
        }
      } else if (imageSrc) {
        preloadImageSource(imageSrc);
        popupImage.onload = finishLoading;
        popupImage.onerror = finishLoading;
        popupImage.src = imageSrc;
        popupImage.alt = card.querySelector('img')?.alt || '';
        popupImage.hidden = false;

        if (popupImage.complete) {
          finishLoading();
        }
      } else {
        finishLoading();
      }

      renderProduct(card);
      updatePopupNav();
    }

    function openPopup(index) {
      if (!mediaCards.length) return;

      popupIndex = ((index % mediaCards.length) + mediaCards.length) % mediaCards.length;
      stopPreviewCycle();
      pausePreviewVideos();
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      popup.classList.add('is-open', 'is-loading');
      popup.setAttribute('aria-hidden', 'false');
      renderPopupMedia();
      preloadMediaAround(popupIndex);
    }

    function closePopup() {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      popupVideo.pause();
      popupVideo.removeAttribute('src');
      popupVideo.innerHTML = '';
      popupImage.removeAttribute('src');
      popup.classList.remove('is-loading');
      document.body.style.overflow = previousBodyOverflow;
      startPreviewCycle();
    }

    function movePopup(direction) {
      if (!mediaCards.length) return;

      popupIndex = ((popupIndex + direction) % mediaCards.length + mediaCards.length) % mediaCards.length;
      renderPopupMedia();
      preloadMediaAround(popupIndex);

      const card = mediaCards[popupIndex];
      if (card) {
        const targetLeft = card.offsetLeft - (viewport.clientWidth - card.offsetWidth) / 2;
        viewport.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
        window.setTimeout(updateArrows, 320);
      }
    }

    function openFromButton(button, force = false) {
      if ((didDrag || suppressNextClick) && !force) return;

      const card = button.matches('[data-ss-reels-card]')
        ? button
        : button.closest('[data-ss-reels-card]');
      const index = mediaCards.indexOf(card);

      if (index >= 0) {
        openPopup(index);
      }
    }

    registry.roots.set(root, { openFromButton });

    previousButton?.addEventListener('click', () => scrollByDirection(-1));
    nextButton?.addEventListener('click', () => scrollByDirection(1));
    viewport.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);

    cards.forEach((card) => {
      const video = getVideo(card);

      card.addEventListener('mouseenter', () => {
        if (!video) return;
        stopPreviewCycle();
        pausePreviewVideos();
        setVideoDefaults(video);
        video.play().catch(() => {});
      });

      card.addEventListener('mouseleave', () => {
        if (popup.classList.contains('is-open')) return;
        startPreviewCycle();
      });
    });

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ss-reels-open], [data-ss-reels-card]');
      if (!button || !root.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      if (suppressNextClick) {
        return;
      }

      openFromButton(button);
    }, true);

    closeButton?.addEventListener('click', closePopup);
    popupPrevious?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      movePopup(-1);
    });

    popupNext?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      movePopup(1);
    });

    popup.addEventListener('click', (event) => {
      if (event.target === popup) closePopup();
    });

    document.addEventListener('keydown', (event) => {
      if (!popup.classList.contains('is-open')) return;

      if (event.key === 'Escape') closePopup();
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        movePopup(-1);
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        movePopup(1);
      }
    });

    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      isPointerDown = true;
      didDrag = false;
      pointerStartButton = event.target.closest('[data-ss-reels-open], [data-ss-reels-card]');
      pointerStartX = event.clientX;
      scrollStartX = viewport.scrollLeft;
      root.classList.add('is-dragging');
      viewport.setPointerCapture?.(event.pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!isPointerDown) return;
      const distance = event.clientX - pointerStartX;
      if (Math.abs(distance) > 10) didDrag = true;
      viewport.scrollLeft = scrollStartX - distance;
    });

    const endPointer = (event) => {
      if (!isPointerDown) return;
      const distance = Math.abs(event.clientX - pointerStartX);
      const scrollDistance = Math.abs(viewport.scrollLeft - scrollStartX);
      const shouldOpen = event.type === 'pointerup' && pointerStartButton && distance <= 10 && scrollDistance <= 10;

      isPointerDown = false;
      root.classList.remove('is-dragging');
      viewport.releasePointerCapture?.(event.pointerId);

      if (shouldOpen) {
        suppressNextClick = true;
        openFromButton(pointerStartButton, true);
      }

      window.setTimeout(() => {
        didDrag = false;
        suppressNextClick = false;
        pointerStartButton = null;
      }, shouldOpen ? 350 : 0);

      updateArrows();
    };

    viewport.addEventListener('pointerup', endPointer);
    viewport.addEventListener('pointercancel', endPointer);
    viewport.addEventListener('pointerleave', endPointer);

    updateArrows();
    startPreviewCycle();
    window.setTimeout(() => preloadMediaAround(0), 350);
  }

  function initAll() {
    document.querySelectorAll(selector).forEach(init);
  }

  function handleDocumentOpen(event) {
    openTrigger(event.target, event);
  }

  if (!registry.bound) {
    document.addEventListener('click', handleDocumentOpen, true);
    registry.bound = true;
  }

  window.SSProductReels = window.SSProductReels || {};
  window.SSProductReels.open = (trigger) => openTrigger(trigger);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
